import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from "react";
import { useStudioAvailability, useWidgetConfig, FloatingButtonWidget } from "@brandclub/bc-widgets";
import { track } from "../analytics/AnalyticsService.js";
import { AnalyticsTrackingEvent } from "../analytics/events.js";
import { useBrandAppConfig } from "../api/cache/appConfigQueryOptions.js";
import { useWidgetVisibilityTracking } from "./hooks/useWidgetVisibilityTracking.js";
import { useConciergeSettings } from "./settings/useBrandclubSettings.js";
const getShadowStyle = (state, resolveColor) => {
  const { shadowColor: shadowColorValue, shadowOpacity, shadowBlur, shadowSpread, shadowOffsetY } = state;
  const shadowColor = resolveColor(shadowColorValue) || "#000000";
  const r = parseInt(shadowColor.slice(1, 3), 16);
  const g = parseInt(shadowColor.slice(3, 5), 16);
  const b = parseInt(shadowColor.slice(5, 7), 16);
  return `0 ${shadowOffsetY}px ${shadowBlur}px ${shadowSpread}px rgba(${r}, ${g}, ${b}, ${shadowOpacity})`;
};
const setImportant = (el, styles) => {
  if (!el) return;
  for (const [k, v] of Object.entries(styles)) el.style.setProperty(k, v, "important");
};
const forceIconSize = (size) => (el) => setImportant(el, {
  width: `${size}px`,
  height: `${size}px`,
  "max-width": "none",
  "max-height": "none",
  "min-width": "0",
  "min-height": "0"
});
const forceButtonLayout = (size) => (el) => setImportant(el, {
  width: `${size}px`,
  height: `${size}px`,
  "max-width": "none",
  "max-height": "none",
  padding: "0",
  // content-box so theme borderWidth draws outside the sized circle
  // (border-box would eat into the fixed size instead)
  "box-sizing": "content-box"
});
const getPositionStyles = (state) => {
  const { position, horizontalOffset, verticalOffset } = state;
  const styles = {};
  if (position.startsWith("bottom")) {
    styles.bottom = verticalOffset;
  } else {
    styles.top = verticalOffset;
  }
  if (position.endsWith("left")) {
    styles.left = horizontalOffset;
  } else {
    styles.right = horizontalOffset;
  }
  return styles;
};
const ChatAiIcon = ({ size, color }) => /* @__PURE__ */ jsx("svg", { width: size, height: size, viewBox: "0 0 22 21", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M15.4136 0.86523C15.6886 0.33825 16.2385 0 16.8436 0C17.5138 0 18.1155 0.41595 18.3528 1.04398L19.0641 2.92734C19.0641 2.92938 19.0641 2.93146 19.0678 2.93317C19.0678 2.93451 19.0711 2.93589 19.0711 2.93659L20.9545 3.6468C21.5836 3.88362 22 4.48481 22 5.15614C22 5.72264 21.7041 6.23893 21.2332 6.5287C21.728 7.70122 22 8.97786 22 10.3123C22 15.9902 17.0844 20.6245 11 20.6245C9.15053 20.6245 7.40432 20.1948 5.87502 19.4382C4.58938 20.0418 3.17629 20.3976 1.99379 20.6C1.34462 20.7111 0.687877 20.4375 0.309707 19.8975C-0.0684628 19.3574 -0.102773 18.6476 0.220347 18.078C0.739387 17.1527 1.07631 16.1081 1.29603 15.1692C0.467657 13.7204 0.00037716 12.067 0.00037716 10.3123C0.00037716 4.63434 4.91591 0 11 0C12.5709 0 14.0627 0.30902 15.4136 0.86523H15.4136ZM11 8.93732C11.7599 8.93732 12.375 9.55329 12.375 10.3123C12.375 11.0712 11.7599 11.6872 11 11.6872C10.2405 11.6872 9.62526 11.0712 9.62526 10.3123C9.62526 9.55329 10.2405 8.93732 11 8.93732ZM5.84414 8.93732C6.60342 8.93732 7.2189 9.55329 7.2189 10.3123C7.2189 11.0712 6.60342 11.6872 5.84414 11.6872C5.08425 11.6872 4.4689 11.0712 4.4689 10.3123C4.4689 9.55329 5.08426 8.93732 5.84414 8.93732ZM16.6201 1.52896C16.6547 1.43619 16.7439 1.375 16.8436 1.375C16.9434 1.375 17.0327 1.43619 17.0673 1.52896L17.7752 3.41269C17.8473 3.59763 17.9539 3.76535 18.0949 3.90524C18.236 4.04516 18.4009 4.15341 18.5864 4.22323L20.4702 4.93337C20.563 4.96843 20.6248 5.05712 20.6248 5.15612C20.6248 5.25512 20.563 5.3438 20.4702 5.37884L18.5864 6.08905C18.4009 6.15881 18.236 6.26712 18.0949 6.40704C17.9539 6.5469 17.8473 6.71466 17.7752 6.89956L17.0673 8.78329C17.0327 8.87609 16.9434 8.93731 16.8436 8.93731C16.7439 8.93731 16.6547 8.87609 16.6201 8.78329L15.912 6.89956C15.8401 6.71465 15.7332 6.5469 15.5925 6.40704C15.4514 6.26712 15.2865 6.15881 15.101 6.08905L13.2172 5.37884C13.1242 5.34381 13.0626 5.25512 13.0626 5.15612C13.0626 5.05712 13.1242 4.96844 13.2172 4.93337L15.101 4.22323C15.2865 4.15341 15.4514 4.04516 15.5925 3.90524C15.7332 3.76535 15.8399 3.59762 15.912 3.41269L16.6201 1.52896Z",
    fill: color
  }
) });
const SearchSparkleIcon = ({ size, color }) => /* @__PURE__ */ jsxs("svg", { width: size, height: size, viewBox: "0 0 32 32", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M7.56264 21.5063C6.13139 21.5588 5.16264 22.5338 4.30771 23.3938C4.13521 23.5663 3.96772 23.7376 3.78022 23.9138L1.49274 26.1938C0.592743 27.0938 -0.118508 27.805 0.0165053 28.7688C0.116505 29.4725 0.556503 29.98 0.927755 30.4088C1.77524 31.3288 2.42399 31.8088 3.03151 31.9563C3.03901 31.9563 3.04776 31.96 3.059 31.9638L3.1065 31.9713C3.1265 31.9713 3.1465 31.9788 3.1665 31.9838C3.259 31.9963 3.3465 32 3.43025 32C4.2815 32 4.85025 31.4362 5.45024 30.84C5.58649 30.7038 5.734 30.56 5.914 30.3875L6.91776 29.3875C7.50901 28.7963 8.10901 28.2 8.59274 27.7163C9.58023 26.74 11.2365 25.1125 10.1765 23.0213C9.729 22.0976 8.66151 21.4701 7.56525 21.5064L7.56264 21.5063ZM7.45889 26.5812L4.79639 29.2437C4.62014 29.4075 4.46388 29.5637 4.32015 29.7075C3.77639 30.2512 3.58391 30.4075 3.3964 30.3987C3.26015 30.3587 2.88514 30.1712 2.12016 29.3425C1.86016 29.0425 1.6364 28.7825 1.60392 28.5463C1.58017 28.37 2.23517 27.7188 2.62768 27.3263L4.89516 25.0625C5.07891 24.89 5.26265 24.7063 5.44641 24.5188C6.15766 23.8025 6.82641 23.1313 7.62139 23.1025C8.0739 23.0863 8.56515 23.3625 8.74888 23.73C9.20138 24.6263 8.67638 25.39 7.46889 26.5775L7.45889 26.5812Z",
      fill: color
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M24.2466 8.35035C24.3229 8.1341 24.4704 7.75411 24.3341 7.61411C24.1941 7.47786 23.8141 7.62161 23.5979 7.70161C21.5266 8.5491 20.0752 8.58161 17.9678 7.71411C16.9678 7.29035 16.9915 7.71411 17.3315 8.51411C18.1478 10.5741 18.1153 11.933 17.2715 14.0242C17.1915 14.2517 17.0353 14.6042 17.1553 14.7717L17.1715 14.788C17.344 14.908 17.6915 14.7517 17.919 14.6717C20.0103 13.8242 21.3739 13.7955 23.4291 14.6117C24.2491 14.9592 24.6454 14.9555 24.2291 13.9755C23.3616 11.868 23.3979 10.4168 24.2416 8.34536L24.2466 8.35035Z",
      fill: color
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M28.4786 2.65522C25.9474 0.460241 22.236 -0.435969 18.3285 0.200242C12.9622 0.871492 9.35871 5.45915 9.55871 11.3575C9.51871 14.325 10.43 16.9124 12.0425 18.8236L10.415 20.4511C10.1025 20.7636 10.1025 21.2711 10.415 21.5824C10.5712 21.7386 10.775 21.8186 10.9787 21.8186C11.1825 21.8186 11.3862 21.7386 11.5425 21.5824L13.1662 19.9586C14.5625 21.1386 16.3049 21.9186 18.3012 22.1786C19.1374 22.3149 19.9611 22.3824 20.7649 22.3824C23.7324 22.3824 26.4475 21.4661 28.4422 19.7511C30.7822 17.7399 31.9937 14.8325 31.9537 11.3413V11.205C32.0262 7.65011 30.8224 4.69146 28.475 2.65493L28.4786 2.65522ZM30.3574 11.3416V11.3541C30.3936 14.3578 29.3736 16.8405 27.4024 18.5365C25.2225 20.4114 21.9998 21.1602 18.5323 20.5914C14.0497 20.0077 11.086 16.2965 11.1585 11.3541V11.3141C11.006 7.04669 13.226 2.44404 18.5571 1.77658C19.3046 1.65658 20.0446 1.59658 20.7646 1.59658C23.3721 1.59658 25.7273 2.38033 27.427 3.86033C29.402 5.57531 30.4182 8.10298 30.3545 11.183V11.3392L30.3574 11.3416Z",
      fill: color
    }
  )
] });
const ThreeSparklesIcon = ({ size, color }) => /* @__PURE__ */ jsx("svg", { width: size, height: size, viewBox: "0 0 32 32", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M5.5795 9.3888C5.25574 6.76006 4.58825 6.09254 1.95977 5.76907C1.73477 5.74157 1.73477 5.41656 1.95977 5.38907C4.58851 5.06531 5.25603 4.39907 5.5795 1.76934C5.607 1.54434 5.93201 1.54434 5.9595 1.76934C6.28326 4.39808 6.95075 5.0656 9.57923 5.38907C9.80423 5.41657 9.80423 5.74158 9.57923 5.76907C6.95049 6.09283 6.28297 6.75907 5.9595 9.3888C5.932 9.6138 5.60699 9.6138 5.5795 9.3888ZM30.0419 26.2315C27.4132 25.9077 26.7456 25.2402 26.4222 22.6117C26.3947 22.3867 26.0697 22.3867 26.0422 22.6117C25.7184 25.2405 25.0509 25.908 22.4224 26.2315C22.1974 26.259 22.1974 26.584 22.4224 26.6115C25.0512 26.9352 25.7187 27.6015 26.0422 30.2312C26.0697 30.4562 26.3947 30.4562 26.4222 30.2312C26.7459 27.6025 27.4134 26.9349 30.0419 26.6115C30.2669 26.584 30.2669 26.259 30.0419 26.2315ZM25.7982 15.0976C19.0368 14.2663 17.3208 12.5501 16.4907 5.79014C16.4194 5.21139 15.5832 5.21139 15.512 5.79014C14.6807 12.5515 12.9645 14.2675 6.20449 15.0976C5.62574 15.1689 5.62574 16.0051 6.20449 16.0763C12.9658 16.9076 14.6818 18.6238 15.512 25.3838C15.5832 25.9626 16.4194 25.9626 16.4907 25.3838C17.322 18.6225 19.0382 16.9065 25.7982 16.0763C26.3769 16.0051 26.3769 15.1689 25.7982 15.0976Z",
    fill: color
  }
) });
const DiamondSparklesIcon = ({ size, color }) => /* @__PURE__ */ jsxs("svg", { width: size, height: size, viewBox: "0 0 29 32", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M28.8011 15.6931C24.2989 14.0296 20.7487 10.4791 19.0802 5.97229C18.9735 5.68559 18.5718 5.68559 18.4652 5.97229C16.8017 10.4745 13.2511 14.0247 8.74432 15.6931C8.45762 15.7998 8.45762 16.2015 8.74432 16.3082C13.2465 17.9717 16.7967 21.5223 18.4652 26.0291C18.5718 26.3158 18.9735 26.3158 19.0802 26.0291C20.7437 21.5269 24.2943 17.9766 28.8011 16.3082C29.0878 16.2015 29.0878 15.7998 28.8011 15.6931Z",
      fill: color
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M10.1329 4.96561C7.87933 4.13385 6.10403 2.35869 5.2723 0.105011C5.22063 -0.0350037 5.01561 -0.0350037 4.96561 0.105011C4.13384 2.35858 2.35869 4.13388 0.105011 4.96561C-0.0350036 5.01728 -0.0350036 5.22231 0.105011 5.27231C2.35858 6.10408 4.13387 7.87924 4.96561 10.1329C5.01728 10.2729 5.2223 10.2729 5.2723 10.1329C6.10407 7.87934 7.87922 6.10404 10.1329 5.27231C10.2729 5.22064 10.2729 5.01562 10.1329 4.96561Z",
      fill: color
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M11.8735 28.4863C10.3716 27.9313 9.18983 26.7494 8.63479 25.2476C8.60145 25.1543 8.46477 25.1543 8.42977 25.2476C7.87472 26.7494 6.69291 27.9313 5.1911 28.4863C5.09776 28.5196 5.09776 28.6563 5.1911 28.6913C6.69291 29.2464 7.87472 30.4282 8.42977 31.93C8.4631 32.0233 8.59978 32.0233 8.63479 31.93C9.18983 30.4282 10.3716 29.2464 11.8735 28.6913C11.9668 28.658 11.9668 28.5213 11.8735 28.4863Z",
      fill: color
    }
  )
] });
const SearchStarIcon = ({ size, color }) => /* @__PURE__ */ jsxs("svg", { width: size, height: size, viewBox: "0 0 30 30", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M14.5 6C14.9669 6 15.3721 6.32312 15.4756 6.77832L16.3066 10.4336C16.5626 11.5589 17.4411 12.4374 18.5664 12.6934L22.2217 13.5244C22.6769 13.6279 23 14.0332 23 14.5C23 14.9669 22.6769 15.3721 22.2217 15.4756L18.5664 16.3066C17.4411 16.5626 16.5626 17.4411 16.3066 18.5664L15.4756 22.2217C15.3721 22.6769 14.9669 23 14.5 23C14.0332 23 13.6279 22.6769 13.5244 22.2217L12.6934 18.5664C12.4374 17.4411 11.5589 16.5626 10.4336 16.3066L6.77832 15.4756C6.32312 15.3721 6 14.9669 6 14.5C6 14.0332 6.32312 13.6279 6.77832 13.5244L10.4336 12.6934C11.5589 12.4374 12.4374 11.5589 12.6934 10.4336L13.5244 6.77832C13.6279 6.32312 14.0332 6 14.5 6ZM14.5 11.3848C14.0004 12.8485 12.8485 14.0004 11.3848 14.5C12.8485 14.9997 14.0004 16.1515 14.5 17.6152C14.9997 16.1515 16.1515 14.9997 17.6152 14.5C16.1515 14.0004 14.9997 12.8485 14.5 11.3848Z",
      fill: color
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M4.24316 4.24316C9.90081 -1.41444 19.0738 -1.41444 24.7314 4.24316C30.1513 9.66312 30.3786 18.309 25.4141 24L29.707 28.293C30.0976 28.6835 30.0976 29.3165 29.707 29.707C29.3165 30.0976 28.6835 30.0976 28.293 29.707L24 25.4141C18.309 30.3786 9.66312 30.1513 4.24316 24.7314C-1.41444 19.0738 -1.41444 9.90081 4.24316 4.24316ZM23.3174 5.65723C18.4408 0.780632 10.5338 0.780632 5.65723 5.65723C0.780632 10.5338 0.780632 18.4408 5.65723 23.3174C10.5338 28.194 18.4408 28.194 23.3174 23.3174C28.194 18.4408 28.194 10.5338 23.3174 5.65723Z",
      fill: color
    }
  )
] });
const SearchSparkleAltIcon = ({ size, color }) => /* @__PURE__ */ jsx("svg", { width: size, height: size, viewBox: "0 0 26 33", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M11.2715 8.41504C11.7891 8.41504 12.209 8.83488 12.209 9.35254C12.2089 9.83275 11.8474 10.2276 11.3818 10.2822L11.375 10.29H11.2715C6.22032 10.29 2.12516 14.3854 2.125 19.4365C2.125 24.4878 6.22022 28.583 11.2715 28.583C16.3226 28.5828 20.418 24.4877 20.418 19.4365C20.4181 18.919 20.8379 18.499 21.3555 18.499C21.8729 18.4991 22.2928 18.9191 22.293 19.4365C22.293 22.1425 21.315 24.618 19.6963 26.5361L24.3086 31.1494C24.6758 31.5147 24.6761 32.108 24.3096 32.4736C23.944 32.8411 23.35 32.8421 22.9844 32.4746V32.4736L18.3711 27.8623C16.4528 29.4809 13.9771 30.458 11.2715 30.458C5.18454 30.4579 0.250088 25.5235 0.25 19.4365C0.25 13.3495 5.18448 8.41512 11.2715 8.41504ZM19.916 5.39355C20.019 4.80866 20.858 4.80851 20.9609 5.39355C21.3483 7.6173 23.0907 9.35979 25.3145 9.74707L25.418 9.77441C25.8975 9.95202 25.8628 10.6955 25.3145 10.792C23.1601 11.1673 21.4583 12.8141 21.002 14.9385L20.9609 15.1455C20.8644 15.694 20.1205 15.7284 19.9434 15.248L19.916 15.1455C19.5407 12.9911 17.8939 11.2883 15.7695 10.832L15.5625 10.792C14.9776 10.6891 14.9775 9.84999 15.5625 9.74707L15.7695 9.70605C17.8939 9.24964 19.5408 7.54786 19.916 5.39355ZM13.6172 0.588867C13.6954 0.136853 14.3476 0.137006 14.4258 0.588867L14.4736 0.816406C14.76 1.93339 15.6761 2.78969 16.8262 2.99023C17.2787 3.0684 17.2781 3.71945 16.8252 3.79688C15.5988 4.01107 14.6398 4.97179 14.4258 6.19824C14.3479 6.65089 13.695 6.65075 13.6172 6.19824C13.4032 4.97154 12.4435 4.01177 11.2168 3.79785V3.79688C10.7647 3.71866 10.7648 3.06837 11.2168 2.99023L11.4434 2.94141C12.5607 2.65526 13.4166 1.73913 13.6172 0.588867Z",
    fill: color
  }
) });
const renderFloatingIcon = (iconStyle, size, color) => {
  switch (iconStyle) {
    case "search-sparkle":
      return /* @__PURE__ */ jsx(SearchSparkleIcon, { size, color });
    case "three-sparkles":
      return /* @__PURE__ */ jsx(ThreeSparklesIcon, { size, color });
    case "diamond-sparkles":
      return /* @__PURE__ */ jsx(DiamondSparklesIcon, { size, color });
    case "search-star":
      return /* @__PURE__ */ jsx(SearchStarIcon, { size, color });
    case "search-sparkle-alt":
      return /* @__PURE__ */ jsx(SearchSparkleAltIcon, { size, color });
    case "chat-ai":
    default:
      return /* @__PURE__ */ jsx(ChatAiIcon, { size, color });
  }
};
const ConciergeFloatingButton = ({
  widgetState,
  isConciergeEnabled = true,
  hideOnConciergePage = false,
  isMaximized = false,
  onSuggestedPillClick,
  currentPathname
}) => {
  const { toggle, isOpen, close, open } = widgetState;
  const [isHovered, setIsHovered] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const wasOpenRef = useRef(isOpen);
  const settings = useConciergeSettings();
  const studioAvailability = useStudioAvailability();
  const floatingButtonConfig = useWidgetConfig("floating-button", {});
  const { data: brandConfig } = useBrandAppConfig();
  const { trackImmediate } = useWidgetVisibilityTracking({
    appName: brandConfig == null ? void 0 : brandConfig.appName,
    appConfigId: brandConfig == null ? void 0 : brandConfig.appConfigId,
    widgetType: "floating_button",
    enabled: isConciergeEnabled && !settings.hide_default_launcher && !!(brandConfig == null ? void 0 : brandConfig.appName),
    pageKey: currentPathname
  });
  useEffect(() => {
    if ((brandConfig == null ? void 0 : brandConfig.appName) && isConciergeEnabled && !settings.hide_default_launcher) {
      trackImmediate();
    }
  }, [brandConfig == null ? void 0 : brandConfig.appName, isConciergeEnabled, settings.hide_default_launcher, trackImmediate]);
  useCallback(
    (message, index, stacklineSku) => {
      track(AnalyticsTrackingEvent.CONCIERGE_SUGGESTED_PILL_CLICKED, {
        message,
        messageIndex: index,
        source: "minimized_floating_button",
        stacklineSku
      });
      onSuggestedPillClick == null ? void 0 : onSuggestedPillClick(message, stacklineSku);
      open();
    },
    [onSuggestedPillClick, open]
  );
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setShouldAnimate(true);
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 350);
      return () => clearTimeout(timer);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        close();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, close]);
  if (isMaximized || hideOnConciergePage || !isConciergeEnabled || settings.hide_default_launcher || (studioAvailability == null ? void 0 : studioAvailability.conciergeIcon) === false) {
    return null;
  }
  return /* @__PURE__ */ jsx(FloatingButtonWidget, { widgetId: "floating-button", displayName: "Floating Button", children: (state, resolveColor) => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("style", { children: `
            @keyframes button-pop-in {
              0% {
                opacity: 0;
                transform: scale(0.5);
              }
              70% {
                opacity: 1;
                transform: scale(1.1);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
            .concierge-button-animate-in {
              animation: button-pop-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
            }
          ` }),
    !isOpen && floatingButtonConfig.featurePromptText && (studioAvailability == null ? void 0 : studioAvailability.featureIconPrompts) !== false && (() => {
      const pillBg = resolveColor(state.featurePromptBgColor);
      const pillText = resolveColor(state.featurePromptTextColor);
      return /* @__PURE__ */ jsx(
        "button",
        {
          onClick: () => {
            open();
          },
          style: {
            position: "fixed",
            ...state.position.startsWith("bottom") ? { bottom: state.verticalOffset + state.size + 12 } : { top: state.verticalOffset + state.size + 12 },
            ...state.position.endsWith("right") ? { right: state.horizontalOffset } : { left: state.horizontalOffset },
            padding: "10px 16px",
            borderRadius: `${state.featurePromptBorderRadius}px`,
            border: (state.featurePromptBorderWidth ?? 0) > 0 ? `${state.featurePromptBorderWidth}px solid ${resolveColor(state.featurePromptBorderColor)}` : "none",
            boxSizing: "border-box",
            backgroundColor: pillBg,
            color: pillText,
            fontSize: `${state.featurePromptFontSize}px`,
            fontWeight: state.featurePromptFontWeight,
            cursor: "pointer",
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.12)",
            zIndex: settings.z_index - 1,
            maxWidth: "280px",
            textAlign: "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            transition: "opacity 0.2s ease"
          },
          "aria-label": floatingButtonConfig.featurePromptText,
          children: floatingButtonConfig.featurePromptText
        }
      );
    })(),
    /* @__PURE__ */ jsx(
      "button",
      {
        ref: forceButtonLayout(state.size),
        onClick: toggle,
        className: shouldAnimate ? "concierge-button-animate-in" : "",
        style: {
          position: "fixed",
          ...getPositionStyles(state),
          width: state.size,
          height: state.size,
          minWidth: state.size,
          minHeight: state.size,
          backgroundColor: isHovered ? resolveColor(state.backgroundColorHover) : resolveColor(state.backgroundColor),
          borderRadius: `${state.borderRadius}%`,
          boxShadow: getShadowStyle(state, resolveColor),
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: settings.z_index,
          transition: shouldAnimate ? "none" : `transform ${state.animationDuration}ms ease, background-color ${state.animationDuration}ms ease`,
          border: state.borderWidth > 0 ? `${state.borderWidth}px solid ${resolveColor(state.borderColor)}` : "none",
          outline: "none",
          transform: !shouldAnimate && isHovered ? `scale(${state.hoverScale})` : !shouldAnimate ? "scale(1)" : void 0
        },
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        "aria-label": isOpen ? "Close Concierge" : "Open Concierge",
        children: isOpen ? /* @__PURE__ */ jsx("svg", { width: "19", height: "11", viewBox: "0 0 19 11", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M1.25 1.25L9.1543 9.1543L17.0586 1.25",
            stroke: "#ffffff",
            strokeWidth: "2.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ) }) : state.customIconUrl ? /* @__PURE__ */ jsx(
          "img",
          {
            ref: forceIconSize(state.iconSize),
            src: state.customIconUrl,
            alt: "",
            style: { width: state.iconSize, height: state.iconSize }
          }
        ) : settings.custom_launcher_icon ? /* @__PURE__ */ jsx(
          "img",
          {
            ref: forceIconSize(state.iconSize),
            src: settings.custom_launcher_icon,
            alt: "",
            style: { width: state.iconSize, height: state.iconSize }
          }
        ) : renderFloatingIcon(state.iconStyle, state.iconSize, resolveColor(state.iconColor))
      }
    )
  ] }) });
};
export {
  ConciergeFloatingButton
};
//# sourceMappingURL=ConciergeFloatingButton.js.map
